import React from "react";
import { useField, ErrorMessage } from "formik";
import { FormField } from "@avamae/formbuilder";
import {
  WorkflowFields,
  IWorkflowTrigger,
  IWorkflowActions,
} from "pages/WorkflowConfiguration/models";

const ErrorM: React.FC<{ name: string }> = props => (
  <ErrorMessage name={props.name}>
    {msg => <div className="ErrorLabel">{msg}</div>}
  </ErrorMessage>
);

interface Props {
  fieldName: string;
  label: string;
  readOnly?: boolean;
}

const returnWorkflowTriggerTypeList = (trigger: IWorkflowTrigger) => {
  if (trigger.workflowTriggerTypeName === "Document Request") {
    return [
      trigger.documentTypesName != null
        ? `Document type - ${trigger.documentTypesName}`
        : "No document type",
      trigger.contactTypesNames != null
        ? `Contact type - ${trigger.contactTypesNames}`
        : "No contact type",
      trigger.userPoolsName != null
        ? `User pools - ${trigger.userPoolsName}`
        : "No user pools",

      trigger.bReasonCode != null
        ? trigger.bReasonCode === true
          ? `Reason code - Yes`
          : "Reason code - No"
        : "Reason code - No",
      trigger.bMessageText != null
        ? trigger.bMessageText === true
          ? `Message text - Yes`
          : "Message text - No"
        : "Message text - No",
    ];
  } else if (trigger.workflowTriggerTypeName === "Document Upload") {
    return [
      trigger.documentTypesName != null
        ? `Document type - ${trigger.documentTypesName}`
        : "No document type",
      trigger.projectTypesNames != null
        ? `Project type - ${trigger.projectTypesNames}`
        : "No project type",
    ];
  } else if (trigger.workflowTriggerTypeName === "Milestone Update") {
    return [
      trigger.milestonesName
        ? `Milestone - ${trigger.milestonesName}`
        : "No milestones name",
      trigger.projectTypesNames != null
        ? `Project types - ${trigger.projectTypesNames}`
        : "No project type",
      trigger.triggerDates != null
        ? `Trigger dates - ${trigger.triggerDates}`
        : "No trigger dates",
      trigger.bTriggeredByCompletingMilestone != null
        ? trigger.bTriggeredByCompletingMilestone === true
          ? `Triggered by completing milestone - Yes`
          : "Triggered by completing milestone - No"
        : "Triggered by completing milestone - No",
    ];
  } else {
    return [];
  }
};

const returnWorkflowActionTypeList = (action: IWorkflowActions) => {
  if (action.workflowActionTypesName === "Update Milestone/Task") {
    return [
      action.updateTasksName != null
        ? `Milestone/Task - ${action.updateTasksName}`
        : "No milestone/task name",
      action.updateDate != null
        ? `Date to update - ${action.updateDate}`
        : "No date to update",
      action.bOverwriteTask ? "Overwrite task - Yes" : "Overwrite task - No",
    ];
  } else if (action.workflowActionTypesName === "Workflow Action") {
    return [
      action.messageText != null
        ? `Message text - ${action.messageText}`
        : "No message text",
      action.documentTypesName != null
        ? `Document type - ${action.documentTypesName}`
        : "No document type",
      action.contactTypesNames != null
        ? `Contact types - ${action.contactTypesNames}`
        : "No contact type",
      action.userPoolsName != null
        ? `User pool - ${action.userPoolsName}`
        : "No user pools",
      action.bEmailNotifications ? "Email notifications - Yes" : "Email notifications - No",

    ];
  } else if (action.workflowActionTypesName === "Workflow Notifications") {
    return [
      action.messageText != null
        ? `Message text - ${action.messageText}`
        : "No message text",
      action.contactTypesNames != null
        ? `Contact types - ${action.contactTypesNames}`
        : "No contact type",
      action.userPoolsName != null
        ? `User pool - ${action.userPoolsName}`
        : "No user pools",
      action.bEmailNotifications ? "Email notifications - Yes" : "Email notifications - No",
    ];
  } else if (action.workflowActionTypesName === "Calculated Date") {
    return [
      action.updateTasksName != null
        ? `Milestone/task to update - ${action.updateTasksName}`
        : "No milestone/task to update",
      action.baseOnTasksName != null
        ? `Milestone/task to base data change on - ${action.baseOnTasksName}`
        : "No milestone/task to base data change on",
      action.taskDayChange != null
        ? `Days to add/subtract - ${action.taskDayChange}`
        : "No days to add/subtract specified",
      action.bBusinessDaysOnly != null
        ? action.bBusinessDaysOnly === true
          ? `Business days only - Yes`
          : "Business days only - No"
        : "Business days only - No",
      // : "Business days not specified",
      action.updateDate != null
        ? `Date to update - ${action.updateDate}`
        : "No date to update",
      action.baseOnDate != null
        ? `Date to base data change on - ${action.baseOnDate}`
        : "No date to base data change on",
      action.bExcludeHolidays != null
        ? action.bExcludeHolidays === true
          ? `Exclude holidays - Yes`
          : "Exclude holidays - No"
        : "Exclude holidays - No",
      // : "Exclude holidays not specified",
    ];
  } else if (action.workflowActionTypesName === "Trigger Workflow") {
    return [
      action.triggersWorkflowDetails !== null &&
      typeof action.triggersWorkflowDetails === "object" &&
      action.triggersWorkflowDetails.hasOwnProperty("bActive") &&
      action.triggersWorkflowDetails.hasOwnProperty("name")
        ? `Workflow - ${action.triggersWorkflowDetails.name} (${
            action.triggersWorkflowDetails.bActive ? "Active" : "Not active"
          })`
        : "No workflow provided",
    ];
  } else if (action.workflowActionTypesName === "Reject Survey") {
    return [
      action.messageText != null
        ? `Message text - ${action.messageText}`
        : "No message text",
    ];
  } else if (action.workflowActionTypesName === "Advanced Calculated Date") {
    return [
      action.updateTasksName != null
        ? `Milestone/task to update - ${action.updateTasksName}`
        : "No milestone/task to update",
      action.baseOnTasksName != null
        ? `Milestone/task to base data change on - ${action.baseOnTasksName}`
        : "No milestone/task to base data change on",
      action.bBusinessDaysOnly != null
        ? action.bBusinessDaysOnly === true
          ? `Business days only - Yes`
          : "Business days only - No"
        : "Business days only - No",
      // : "Business days not specified",
      action.updateDate != null
        ? `Date to update - ${action.updateDate}`
        : "No date to update",
      action.baseOnDate != null
        ? `Date to base data change on - ${action.baseOnDate}`
        : "No date to base data change on",
      action.bExcludeHolidays != null
        ? action.bExcludeHolidays === true
          ? `Exclude holidays - Yes`
          : "Exclude holidays - No"
        : "Exclude holidays - No",
      // : "Exclude holidays not specified",
    ];
  } else {
    return [];
  }
};

const WorkflowBuilderField = function<T>({
  fieldName,
  label,
  readOnly,
}: Props) {
  const [field, meta] = useField<any>(fieldName);
  const showError = meta.touched && meta.error;

  if (fieldName.toLowerCase() === "workflowtrigger") {
    const fieldValue = field.value as WorkflowFields["workflowTrigger"];

    if (readOnly) {
      return fieldValue === null ? null : (
        <div className="FormBox FormSideBySide WorkflowBuilderFieldRenderer ReadOnly">
          <div className="FormLabel">
            <label>{`${label} - ${fieldValue.workflowTriggerTypeName}`}</label>
          </div>
          <div className="FormField">
            <ul>
              {returnWorkflowTriggerTypeList(fieldValue).map((val, idx) => (
                <li key={idx}>{val}</li>
              ))}
            </ul>
          </div>
        </div>
      );
    } else {
      return (
        <div className="FormBox FormSideBySide WorkflowBuilderFieldRenderer">
          <div className="FormLabel">
            <label>{label}</label>
          </div>

          <div className="FormField">TODO</div>
          {showError && <ErrorM name={fieldName.toString()} />}
        </div>
      );
    }
  }

  if (fieldName.toLowerCase() === "workflowactions") {
    const fieldValue = field.value as WorkflowFields["workflowActions"];

    if (readOnly) {
      return fieldValue == null || fieldValue.length === 0 ? (
        <div className="FormBox FormSideBySide WorkflowBuilderFieldRenderer ReadOnly">
          <div className="FormLabel">
            <label>{"No workflow actions"}</label>
          </div>
        </div>
      ) : (
        <div>
          {fieldValue.map(action => {
            return (
              <div className="FormBox FormSideBySide WorkflowBuilderFieldRenderer ReadOnly">
                <div className="FormLabel">
                  <label>{action.workflowActionTypesName}</label>
                </div>
                <div className="FormField">
                  <ul>
                    {returnWorkflowActionTypeList(action).map((val, idx) => (
                      <li key={idx}>{val}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      );
    } else {
      return (
        <div className="FormBox FormSideBySide WorkflowBuilderFieldRenderer">
          <div className="FormLabel">
            <label>{label}</label>
          </div>

          <div className="FormField">TODO</div>
          {showError && <ErrorM name={fieldName.toString()} />}
        </div>
      );
    }
  }

  if (fieldName.toLowerCase() === "workflowapprovals") {
    const fieldValue = field.value as WorkflowFields["workflowApprovals"];

    if (readOnly) {
      return fieldValue == null || fieldValue.length === 0 ? (
        <div className="FormBox FormSideBySide WorkflowBuilderFieldRenderer ReadOnly">
          <div className="FormLabel">
            <label>{"No workflow approvals"}</label>
          </div>
        </div>
      ) : (
        <div>
          {fieldValue.map(approval => {
            return (
              <div className="FormBox FormSideBySide WorkflowBuilderFieldRenderer ReadOnly">
                <div className="FormLabel">
                  <label>{label}</label>
                </div>
                <div className="FormField">
                  <ul>
                    <li>
                      {approval.contactTypesNames != null
                        ? `Contact type - ${approval.contactTypesNames}`
                        : "No contact type"}
                    </li>
                    <li>
                      {approval.userPoolsName != null
                        ? `User pools - ${approval.userPoolsName}`
                        : "No user pools"}
                    </li>
                    <li>
                      {approval.message != null
                        ? `Message - ${approval.message}`
                        : "No message"}
                    </li>
                    <li>
                      {approval.bEmailNotifications != null
                        ? approval.bEmailNotifications === true
                          ? `Email notifications - Yes`
                          : "Email notifications - No"
                        : "Email notifications - No"}
                    </li>
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      );
    } else {
      return (
        <div className="FormBox FormSideBySide WorkflowBuilderFieldRenderer">
          <div className="FormLabel">
            <label>{label}</label>
          </div>

          <div className="FormField">TODO</div>
          {showError && <ErrorM name={fieldName.toString()} />}
        </div>
      );
    }
  }

  if (fieldName.toLowerCase() === "workflowapprovaloutcomes") {
    const fieldValue = field.value as WorkflowFields["workflowApprovalOutcomes"];

    if (readOnly) {
      return fieldValue == null || fieldValue.length === 0 ? (
        <div className="FormBox FormSideBySide WorkflowBuilderFieldRenderer ReadOnly">
          <div className="FormLabel">
            <label>{"No workflow approval outcomes"}</label>
          </div>
        </div>
      ) : (
        <div>
          {fieldValue.map(approvalOutcome => {
            return (
              <div className="FormBox FormSideBySide WorkflowBuilderFieldRenderer ReadOnly">
                <div className="FormLabel">
                  <label>{`Approval outcome - ${approvalOutcome.workflowApprovalOutcomesName}`}</label>
                </div>

                <div className="FormField">
                  <ul>
                    <li>
                      {`Reason code for rejection - ${
                        approvalOutcome.bReasonCode ? "Yes" : "No"
                      }`}
                    </li>
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      );
    } else {
      return (
        <div className="FormBox FormSideBySide WorkflowBuilderFieldRenderer">
          <div className="FormLabel">
            <label>{label}</label>
          </div>

          <div className="FormField">TODO</div>
          {showError && <ErrorM name={fieldName.toString()} />}
        </div>
      );
    }
  }

  return (
    <div style={{ marginBottom: "20px" }}>
      <b>
        Field renderer not defined for a custom field type with this field name.
      </b>
    </div>
  );
};

export { WorkflowBuilderField };

export const workflowBuilderFieldRenderer = (readOnly: boolean) =>
  function workflowBuilderFieldRenderer<T>(
    _key: React.ReactText,
    field: FormField<T>
  ) {
    return (
      <WorkflowBuilderField
        label={field.label as string}
        readOnly={readOnly}
        fieldName={field.name as string}
      />
    );
  };
